/**
 * Bubbles — glass-look circles rising from below surface, popping at edge.
 */

import type { Graphics } from 'pixi.js';
import { VOID } from '../../constants';
import type { VoidState } from './types';
import { visibleX, lerp } from './types';
import { getEdgeY, getInterpolatedEdgeY } from './edge';

export function updateBubbles(gfx: Graphics, dt: number, voidY: number, state: VoidState): void {
  const maxBubbles = state.riseIntensity > 0.1
    ? lerp(VOID.BUBBLE_MAX_CALM, VOID.BUBBLE_MAX_RISE, state.riseIntensity)
    : VOID.BUBBLE_MAX_CALM;
  const spawnRate = state.riseIntensity > 0.1
    ? lerp(VOID.BUBBLE_SPAWN_CALM, VOID.BUBBLE_SPAWN_RISE, state.riseIntensity)
    : VOID.BUBBLE_SPAWN_CALM;

  // Spawn
  if (state.bubbles.length < maxBubbles && Math.random() < spawnRate) {
    const bv = visibleX();
    const radius = VOID.BUBBLE_RADIUS_MIN + Math.random() * (VOID.BUBBLE_RADIUS_MAX - VOID.BUBBLE_RADIUS_MIN);
    const speed = VOID.BUBBLE_SPEED_MIN + Math.random() * (VOID.BUBBLE_SPEED_MAX - VOID.BUBBLE_SPEED_MIN);
    const lifespan = 1.5 + Math.random() * 2.0;
    state.bubbles.push({
      x: bv.off + Math.random() * bv.w,
      y: voidY + 10 + Math.random() * 20, // below surface
      vy: -speed,
      radius,
      phase: Math.random() * Math.PI * 2,
      color: Math.random() < 0.6 ? VOID.EDGE_COLOR : 0x8800ff,
      life: lifespan,
      maxLife: lifespan,
      popping: false,
      popTimer: 0,
    });
  }

  // Update
  for (let i = state.bubbles.length - 1; i >= 0; i--) {
    const b = state.bubbles[i];

    if (b.popping) {
      b.popTimer += dt;
      if (b.popTimer >= VOID.BUBBLE_POP_DURATION) {
        state.bubbles.splice(i, 1);
        continue;
      }
    } else {
      b.life -= dt;
      if (b.life <= 0) {
        state.bubbles.splice(i, 1);
        continue;
      }

      // Move upward with wobble
      b.y += b.vy * dt;
      b.phase += VOID.BUBBLE_WOBBLE_FREQ * dt;
      b.x += Math.sin(b.phase) * VOID.BUBBLE_WOBBLE_AMP * dt * 60;

      // Pop when reaching edge (use cached interpolation when available)
      const edgeY = state.cachedEdgeYs.length > 0
        ? getInterpolatedEdgeY(b.x, state)
        : getEdgeY(b.x, state.lastDrawnVoidY >= 0 ? state.lastDrawnVoidY : b.y, state);
      if (b.y <= edgeY) {
        b.popping = true;
        b.popTimer = 0;
        b.y = edgeY;
      }
    }
  }

  // Draw
  gfx.clear();
  for (const b of state.bubbles) {
    let scale = 1;
    let alpha = 0.6;

    if (b.popping) {
      const popT = b.popTimer / VOID.BUBBLE_POP_DURATION;
      scale = 1 + (VOID.BUBBLE_POP_SCALE - 1) * popT;
      alpha = 0.6 * (1 - popT);
    } else {
      // Fade in from below
      const lifeT = b.life / b.maxLife;
      alpha = Math.min(0.6, lifeT * 0.8);
    }

    const r = b.radius * scale;

    // Circle outline (glass bubble look)
    gfx.circle(b.x, b.y, r);
    gfx.stroke({ color: b.color, width: 1, alpha });

    // Highlight dot at top-left for glass sheen
    if (r > 3) {
      gfx.circle(b.x - r * 0.3, b.y - r * 0.3, r * 0.2);
      gfx.fill({ color: 0xffffff, alpha: alpha * 0.5 });
    }
  }
}
